import { useMemo } from "react";

import GoTo from "@/app/shared/GoTo";
import { selectableColors } from "@/design/colors/colors";
import Valkyrie from "@sippy-platform/valkyrie";

import { Box, ButtonBase, Divider, Stack, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { format, isSameDay, isSameMonth, isSameSecond, isSameYear, startOfDay, subDays, subSeconds } from "date-fns";
import PropTypes from "prop-types";

function CalendarCard({ holiday, onClick, getGoTo }) {
  const color = selectableColors.find((color) => holiday.color === color[500]);

  const overflow = useMemo(() => getGoTo(holiday.overflow), [getGoTo, holiday.overflow]);

  const dateData = useMemo(() => {
    const startDate = holiday.start;
    const endDate = holiday.end;

    // A full day starts with the first second of itself and ends with the first second of the next day
    const isFullDay = isSameSecond(startDate, startOfDay(startDate)) && isSameSecond(endDate, startOfDay(endDate));

    if (isSameDay(startDate, endDate) || isSameDay(startDate, subSeconds(endDate, 1))) {
      if (isFullDay) {
        return `${format(startDate, "d MMMM")}`;
      } else {
        return `${format(startDate, "d MMMM, H:mm")} until ${format(endDate, "H:mm")}`;
      }
    } else if (isSameMonth(startDate, endDate)) {
      if (isFullDay) {
        return `${format(startDate, "d MMMM")} until ${format(subDays(endDate, 1), "d MMMM")}`;
      } else {
        return `${format(startDate, "d MMMM, H:mm")} until ${format(endDate, "d MMMM, H:mm")}`;
      }
    } else {
      const datesCrossYears = !isSameYear(startDate, endDate);

      if (isFullDay) {
        return `${format(startDate, datesCrossYears ? "d MMMM yyyy" : "d MMMM")} until ${format(subDays(endDate, 1), datesCrossYears ? "d MMMM yyyy" : "d MMMM")}`;
      } else {
        return `${format(startDate, datesCrossYears ? "d MMMM yyyy, H:mm" : "d MMMM, H:mm")} until ${format(endDate, datesCrossYears ? "d MMMM yyyy, H:mm" : "d MMMM, H:mm")}`;
      }
    }
  }, [holiday.end, holiday.start]);

  return (
    <Box
      component={ButtonBase}
      onClick={onClick}
      id={`holiday${holiday.id}`}
      sx={(theme) => ({
        border: `1px solid ${grey[300]}`,
        borderLeft: `3px solid ${color[500]}`,
        ...theme.applyStyles("dark", {
          borderColor: grey[800],
          borderLeftColor: color[500],
        }),
        py: 0.5,
        px: 1,
        borderRadius: 1,
        textAlign: "initial",
        display: "flex",
        alignItems: "initial",
        justifyContent: "initial",
        flexDirection: "column",
        ":hover": {
          borderColor: color[500],
          bgcolor: color[50],
          ...theme.applyStyles("dark", {
            bgcolor: color[900],
          }),
          "& > .MuiDivider-root": {
            borderColor: color[500],
          },
        },
      })}
    >
      <Typography
        sx={{
          fontSize: 12,
        }}
      >
        {dateData}
      </Typography>
      <Typography>{holiday.name}</Typography>
      {holiday.overflow && (
        <>
          <Divider sx={{ my: 0.25 }} />
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              gap: 1,
            }}
          >
            <Valkyrie icon={overflow?.icon} />
            <Typography sx={{ fontSize: 13 }}>
              <GoTo target={overflow} hideType />
            </Typography>
          </Stack>
        </>
      )}
    </Box>
  );
}

CalendarCard.propTypes = {
  holiday: PropTypes.object,
  onClick: PropTypes.func,
  getGoTo: PropTypes.func,
};

export default CalendarCard;
